import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  Building2, Users, UserSquare2, Loader2, ChevronDown, ChevronRight,
  CheckCircle, Clock, XCircle, Power, PowerOff, Search, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPlan = (p) => p ? p.charAt(0).toUpperCase() + p.slice(1) : "—";
const statusBadge = (status) => {
  const map = {
    trialing:  { color: "bg-blue-100 text-blue-700",       label: "Trial" },
    active:    { color: "bg-emerald-100 text-emerald-700", label: "Active" },
    past_due:  { color: "bg-amber-100 text-amber-700",     label: "Past Due" },
    canceled:  { color: "bg-rose-100 text-rose-700",       label: "Canceled" },
    incomplete:{ color: "bg-gray-100 text-gray-700",       label: "Incomplete" },
  };
  const m = map[status] || { color: "bg-gray-100 text-gray-700", label: status || "—" };
  return <Badge className={`text-xs ${m.color}`}>{m.label}</Badge>;
};

function PortalStateBadge({ row }) {
  if (row.portal_active) {
    return <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle className="w-3 h-3 mr-1 inline" />Active</Badge>;
  }
  if (row.has_invite) {
    return <Badge className="bg-amber-100 text-amber-700 text-xs"><Clock className="w-3 h-3 mr-1 inline" />Invited</Badge>;
  }
  return <Badge className="bg-gray-100 text-gray-600 text-xs"><XCircle className="w-3 h-3 mr-1 inline" />No Portal</Badge>;
}

function TenantRow({ tenant, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [prefsLoading, setPrefsLoading] = useState(false);

  const fetchPrefs = useCallback(async () => {
    if (prefs) return;
    setPrefsLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/tenants/${tenant.id}/prefs`);
      setPrefs(res.data);
    } catch (e) {
      // Non-fatal — column may not exist yet on legacy DBs
      setPrefs({ auto_send_portal_invite_on_bulk_import: false });
    } finally { setPrefsLoading(false); }
  }, [tenant.id, prefs]);

  useEffect(() => { if (expanded) fetchPrefs(); }, [expanded, fetchPrefs]);

  const togglePref = async (key, val) => {
    try {
      const res = await axios.put(`${API}/owner-console/tenants/${tenant.id}/prefs`, { [key]: val });
      setPrefs(res.data);
      toast.success("Preference updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const toggleUserActive = async (portalUserId, isCurrentlyActive) => {
    if (!portalUserId) return;
    const action = isCurrentlyActive ? "deactivate" : "activate";
    if (!window.confirm(`${isCurrentlyActive ? "Deactivate" : "Reactivate"} this portal user?`)) return;
    try {
      await axios.post(`${API}/owner-console/portal-hierarchy/users/${portalUserId}/${action}`);
      toast.success(`Portal user ${action}d`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  return (
    <Card data-testid={`tenant-card-${tenant.id}`}>
      <CardHeader className="pb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </Button>
            <Building2 className="w-5 h-5 text-blue-600 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold truncate">{tenant.name}</div>
              <div className="text-xs text-muted-foreground truncate">
                Owner: {tenant.owner?.name || "—"} · {tenant.owner?.email || ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">{fmtPlan(tenant.plan)}</Badge>
            {statusBadge(tenant.status)}
            <Badge variant="secondary" className="text-xs">
              <Users className="w-3 h-3 mr-1" />{tenant.counts.employees} emp
            </Badge>
            <Badge variant="secondary" className="text-xs">
              <UserSquare2 className="w-3 h-3 mr-1" />{tenant.counts.clients} clients
            </Badge>
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">{tenant.counts.portal_active} portal active</Badge>
            {tenant.counts.portal_pending > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-xs">{tenant.counts.portal_pending} pending</Badge>
            )}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-4 border-t">
          {/* Tenant Prefs Toggle */}
          <div className="pt-4 p-3 rounded-md bg-muted/30 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <Label className="text-sm font-medium">Auto-send portal invite on bulk client import</Label>
              <div className="text-xs text-muted-foreground mt-0.5">
                When ON, every newly bulk-imported client for this tenant automatically receives a portal-invite email. When OFF (default), the tenant owner must manually click "Send portal invites" after each batch.
              </div>
            </div>
            <Switch
              checked={!!prefs?.auto_send_portal_invite_on_bulk_import}
              onCheckedChange={(v) => togglePref("auto_send_portal_invite_on_bulk_import", v)}
              disabled={prefsLoading}
              data-testid={`tenant-auto-invite-toggle-${tenant.id}`}
            />
          </div>

          {/* Employees */}
          {tenant.employees.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Employees ({tenant.employees.length})
              </div>
              <div className="space-y-1">
                {tenant.employees.map(e => (
                  <div key={e.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-accent/40 text-sm" data-testid={`emp-row-${e.id}`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{e.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{e.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PortalStateBadge row={e} />
                      {e.portal_user_id && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                          onClick={() => toggleUserActive(e.portal_user_id, e.portal_active)}
                          data-testid={`emp-toggle-${e.id}`}>
                          {e.portal_active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Clients */}
          {tenant.clients.length > 0 && (
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Clients ({tenant.clients.length})
              </div>
              <div className="space-y-1">
                {tenant.clients.map(c => (
                  <div key={c.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-accent/40 text-sm" data-testid={`cli-row-${c.id}`}>
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">{c.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <PortalStateBadge row={c} />
                      {c.portal_user_id && (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs"
                          onClick={() => toggleUserActive(c.portal_user_id, c.portal_active)}
                          data-testid={`cli-toggle-${c.id}`}>
                          {c.portal_active ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tenant.employees.length === 0 && tenant.clients.length === 0 && (
            <div className="text-sm text-muted-foreground italic py-4 text-center">
              This tenant has no employees or clients yet.
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

export default function PortalHierarchyPanel() {
  const [data, setData] = useState({ tenants: [], total_tenants: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/owner-console/portal-hierarchy`);
      setData(res.data || { tenants: [], total_tenants: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load hierarchy");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.tenants.filter(t => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    if (t.name?.toLowerCase().includes(s)) return true;
    if (t.owner?.email?.toLowerCase().includes(s)) return true;
    if (t.owner?.name?.toLowerCase().includes(s)) return true;
    if (t.employees.some(e => (e.email || "").toLowerCase().includes(s) || (e.name || "").toLowerCase().includes(s))) return true;
    if (t.clients.some(c => (c.email || "").toLowerCase().includes(s) || (c.name || "").toLowerCase().includes(s))) return true;
    return false;
  });

  return (
    <div className="space-y-4" data-testid="portal-hierarchy-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Portal Hierarchy
              </CardTitle>
              <CardDescription>
                Every paying business tenant on the platform with their employees, clients, and portal status. Click a tenant to expand and manage.
              </CardDescription>
            </div>
            <Button onClick={fetchData} variant="outline" size="sm" data-testid="portal-hierarchy-refresh">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
          </div>
          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tenant, owner email, or portal user..."
              className="pl-9"
              data-testid="portal-hierarchy-search"
            />
          </div>
        </CardHeader>
      </Card>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            {data.total_tenants === 0
              ? "No tenants yet. New subscriber accounts will appear here automatically."
              : "No tenants match your search."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(t => <TenantRow key={t.id} tenant={t} onChanged={fetchData} />)}
        </div>
      )}
    </div>
  );
}
